import { Ref, useMemo } from 'react';
import {
  Column,
  DimensionColumn,
  MetricsTablePreset as Preset,
  PerformanceData
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import {
  MetricsTable,
  MetricsTableHandle,
  OverrideParams
} from '@Components/MetricsTable/MetricsTable';
import {
  CellData,
  ColumnMetaDataMap,
  constructTableData,
  DEFAULT_GET_DIMENSION_CELL,
  DimensionMap,
  getDataHeaders,
  getDimensionDataHeaders
} from '@Components/MetricsTable/metrics-table-utils';
import { ServerMetricValues } from '@Components/MetricsTable/server-metric-values';
import { useExperimentFlag } from '@src/utils/experiment-context';

interface MetricsTableWithPresetsProps {
  columnMetaDataMap?: ColumnMetaDataMap;
  data: PerformanceData[];
  dataFetchKey?: (dataHeader: Column) => string;
  filter?: (dimensions: DimensionMap) => boolean;
  hasAggregateData?: boolean;
  getDimensionCell?: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: unknown[]
  ) => CellData;
  percentAOIPlaceholder?: number;
  preset?: Partial<Preset>;
  role?: number; // We don't want to use redux here because we cannot use it in storybook testing
  variant?: 'Metrics' | 'SparkChart';
  kpiName?: string;
  groupedSegments?: Record<string, GroupedSegment[]>;
  rowLabelPrefix?: (row: PerformanceData) => string;
  // Renders the cells of prefixed rows in italics.
  prefixedCellItalic?: boolean;
  aggregateLabelPrefix?: string;
  // Layout pass-through — see MetricsTable's own prop docs for behavior of each.
  maxHeight?: number;
  fillParentHeight?: boolean;
  fixedWidth?: number;
  overrideParams?: OverrideParams;
  disableMultiSorting?: boolean;
  virtualizeThreshold?: number;
  visibleRowIndices?: ReadonlySet<number>;
  // SQL metric cells + totals; the column aggregators stay the fallback.
  serverValues?: ServerMetricValues;
  ref?: Ref<MetricsTableHandle>;
}

export const MetricsTableWithPresets = ({
  columnMetaDataMap = {},
  data,
  dataFetchKey = () => '',
  filter,
  getDimensionCell = DEFAULT_GET_DIMENSION_CELL,
  hasAggregateData = true,
  percentAOIPlaceholder,
  preset = { dimensionColumns: [], dataColumns: [] },
  role = 10,
  variant = 'Metrics',
  kpiName,
  groupedSegments = {},
  rowLabelPrefix,
  prefixedCellItalic = false,
  aggregateLabelPrefix,
  maxHeight,
  fillParentHeight,
  fixedWidth,
  overrideParams,
  disableMultiSorting,
  virtualizeThreshold,
  visibleRowIndices,
  serverValues,
  ref
}: MetricsTableWithPresetsProps) => {
  const dataHeaders: Column[] = useMemo(() => getDataHeaders(preset, role), [preset, role]);
  const shouldEnableMetricsTableSegmentGroups = Boolean(
    useExperimentFlag('enableMetricsTableSegmentGroups')
  );

  const dimensionDataHeaders: DimensionColumn[] = useMemo(
    () =>
      getDimensionDataHeaders(preset, role, groupedSegments, shouldEnableMetricsTableSegmentGroups),
    [preset, role, groupedSegments, shouldEnableMetricsTableSegmentGroups]
  );

  const {
    aggregateData,
    dimensionData,
    tableData
  }: {
    aggregateData: CellData[];
    dimensionData: Record<string, CellData>[];
    tableData: CellData[][];
  } = useMemo(() => {
    const filteredData = filter && data ? data.filter(row => filter(row.dimensions)) : data;
    const {
      aggregateData: builtAggregateData,
      dimensionData: builtDimensionData,
      tableData: builtTableData
    } = constructTableData(
      columnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      getDimensionCell,
      dataFetchKey,
      filteredData,
      groupedSegments,
      shouldEnableMetricsTableSegmentGroups,
      serverValues
    );

    let anyRowPrefixed = false;
    if (rowLabelPrefix) {
      filteredData.forEach((row, i) => {
        const prefix = rowLabelPrefix(row);
        if (prefix) {
          anyRowPrefixed = true;
          builtTableData[i] = builtTableData[i].map(cell =>
            cell && cell.label !== '-'
              ? {
                  ...cell,
                  ...(prefixedCellItalic && { italic: true }),
                  label: `${prefix}${cell.label}`
                }
              : cell
          );
        }
      });
    }

    if (aggregateLabelPrefix && anyRowPrefixed) {
      for (let i = 0; i < builtAggregateData.length; i++) {
        const cell = builtAggregateData[i];
        if (cell && cell.label !== '-') {
          builtAggregateData[i] = {
            ...cell,
            label: `${aggregateLabelPrefix}${cell.label}`
          };
        }
      }
    }

    return {
      aggregateData: builtAggregateData,
      dimensionData: builtDimensionData,
      tableData: builtTableData
    };
  }, [
    prefixedCellItalic,
    aggregateLabelPrefix,
    columnMetaDataMap,
    data,
    dataFetchKey,
    dataHeaders,
    dimensionDataHeaders,
    filter,
    getDimensionCell,
    groupedSegments,
    rowLabelPrefix,
    serverValues,
    shouldEnableMetricsTableSegmentGroups
  ]);

  return (
    <MetricsTable
      aggregateData={hasAggregateData ? aggregateData : []}
      columnMetaDataMap={columnMetaDataMap}
      dataColumns={dataHeaders}
      defaultSorting={preset.defaultSorting}
      dimensionColumns={dimensionDataHeaders}
      dimensionData={dimensionData}
      disableMultiSorting={disableMultiSorting}
      fillParentHeight={fillParentHeight}
      fixedWidth={fixedWidth}
      groupedSegments={groupedSegments}
      kpiName={kpiName}
      maxHeight={maxHeight}
      overrideParams={overrideParams}
      percentAOIPlaceholder={percentAOIPlaceholder}
      ref={ref}
      role={role}
      superHeaders={preset.superHeaders}
      tableData={tableData}
      variant={variant}
      virtualizeThreshold={virtualizeThreshold}
      visibleRowIndices={visibleRowIndices}
    />
  );
};
